import { HTTP_STATUS } from "./http-status";

/**
 * Stable, machine-readable `code` values in API error bodies. The apps branch on these, so a value
 * never changes once shipped; the message next to it is free to.
 */
export const ERROR_CODES = {
  // Any endpoint
  BAD_REQUEST: "bad_request",
  VALIDATION_FAILED: "validation_failed",
  UNAUTHORIZED: "unauthorized",
  FORBIDDEN: "forbidden",
  NOT_FOUND: "not_found",
  METHOD_NOT_ALLOWED: "method_not_allowed",
  CONFLICT: "conflict",
  DUPLICATE: "duplicate",
  IN_USE: "in_use",
  BUSY: "busy",
  PAYLOAD_TOO_LARGE: "payload_too_large",
  UNSUPPORTED_MEDIA_TYPE: "unsupported_media_type",
  INVALID_JSON: "invalid_json",
  RATE_LIMITED: "rate_limited",
  TIMEOUT: "timeout",
  SERVICE_UNAVAILABLE: "service_unavailable",
  INTERNAL_ERROR: "internal_error",
  HTTP_ERROR: "http_error",
  UNKNOWN_ERROR: "unknown_error",
  /** Set by the apps' API clients, never by the API: the request didn't reach it or got no answer. */
  NETWORK_ERROR: "network_error",

  // Coupons
  INVALID_COUPON: "invalid_coupon",
  COUPON_NOT_ACTIVE: "coupon_not_active",
  COUPON_SCHEDULED: "coupon_scheduled",
  COUPON_EXPIRED: "coupon_expired",
  COUPON_USED_UP: "coupon_used_up",
  COUPON_FIRST_ORDER: "coupon_first_order",
  COUPON_LIMIT_REACHED: "coupon_limit_reached",
  COUPON_MIN_SUBTOTAL: "coupon_min_subtotal",
  COUPON_NO_SAVING: "coupon_no_saving",
  COUPON_SIGN_IN: "coupon_sign_in",
  COUPON_NOT_ELIGIBLE: "coupon_not_eligible",
  COUPON_IN_USE: "coupon_in_use",
  COUPON_USAGE_LIMIT_TOO_LOW: "usage_limit_too_low",
  COUPON_RESUME_EXPIRED: "expired",

  // Invoices
  INVOICE_NOT_READY: "invoice_not_ready",

  // Address search (Google Maps)
  MAPS_UNAVAILABLE: "maps_unavailable",
  PLACE_NOT_FOUND: "place_not_found",

  // Payments and webhooks
  PAYMENT_UNAVAILABLE: "payment_unavailable",
  PAYMENT_NOT_REQUIRED: "payment_not_required",
  PAYMENT_PENDING: "payment_pending",
  PAYMENT_SIGNATURE_INVALID: "payment_signature_invalid",
  PAYMENT_MISMATCH: "payment_mismatch",
  PAYMENT_EXPIRED: "payment_expired",
  BELOW_MINIMUM_ORDER: "below_minimum_order",
  INVALID_DELIVERY_DATE: "invalid_delivery_date",
  ORDERS_CLOSED: "orders_closed",
  CANCEL_CLOSED: "cancel_closed",

  // Delivery slots
  SLOT_UNAVAILABLE: "slot_unavailable",
  SLOT_FULL: "slot_full",
  SLOT_TAKEN: "slot_taken",
  SLOT_IN_USE: "slot_in_use",
  NOT_SERVICED: "not_serviced",
  COD_UNAVAILABLE: "cod_unavailable",
  COD_LIMIT: "cod_limit",
  WEBHOOK_SIGNATURE_INVALID: "webhook_signature_invalid",
  WEBHOOK_NOT_CONFIGURED: "webhook_not_configured",

  // Rules of particular screens and flows (staff and customers see the message; apps may branch on the code)
  ALREADY_GENERATED: "already_generated",
  ALREADY_REGISTERED: "already_registered",
  ALREADY_RESOLVED: "already_resolved",
  ALREADY_SENT: "already_sent",
  BELOW_BOOKED: "below_booked",
  CHANNEL_UNAVAILABLE: "channel_unavailable",
  EXCEEDS_TOTAL: "exceeds_total",
  FILE_TOO_LARGE: "file_too_large",
  IMPORT_FAILED: "import_failed",
  INSUFFICIENT: "insufficient",
  INVALID_ADDRESS: "invalid_address",
  INVALID_AGENT: "invalid_agent",
  INVALID_AMOUNT: "invalid_amount",
  INVALID_CATEGORY: "invalid_category",
  INVALID_CREDENTIALS: "invalid_credentials",
  INVALID_DATE: "invalid_date",
  INVALID_ORDER: "invalid_order",
  INVALID_OTP: "invalid_otp",
  INVALID_PARENT: "invalid_parent",
  INVALID_PLAN_ITEM: "invalid_plan_item",
  INVALID_TRANSITION: "invalid_transition",
  INVALID_UPLOAD: "invalid_upload",
  NOT_A_RIDER: "not_a_rider",
  NOT_CHECKED_IN: "not_checked_in",
  NOT_SUBSCRIBABLE: "not_subscribable",
  CITY_TAKEN: "city_taken",
  COMING_SOON: "coming_soon",
  OFF_DUTY: "off_duty",
  ONLINE_REFUND_USED: "online_refund_used",
  ORIGINAL_UNAVAILABLE: "original_unavailable",
  OTP_COOLDOWN: "otp_cooldown",
  OTP_EXPIRED: "otp_expired",
  OTP_LIMIT: "otp_limit",
  OTP_REQUIRED: "otp_required",
  OUT_OF_STOCK: "out_of_stock",
  PUSH_UNAVAILABLE: "push_unavailable",
  SESSION_EXPIRED: "session_expired",
  SIGN_IN_LOCKED: "sign_in_locked",
  SMS_UNAVAILABLE: "sms_unavailable",
  STORAGE_ERROR: "storage_error",
  TOO_MANY_ADDRESSES: "too_many_addresses",
  TOO_MANY_ATTEMPTS: "too_many_attempts",
  UNAVAILABLE: "unavailable",
  UPLOADS_NOT_CONFIGURED: "uploads_not_configured",
  WALLET_CHANGED: "wallet_changed",
} as const;
export type ErrorCode = (typeof ERROR_CODES)[keyof typeof ERROR_CODES];

/** The code for a bare HTTP error (from Nest or a proxy) that didn't come with one. */
export const ERROR_CODE_BY_STATUS: Partial<Record<number, ErrorCode>> = {
  [HTTP_STATUS.BAD_REQUEST]: ERROR_CODES.BAD_REQUEST,
  [HTTP_STATUS.UNAUTHORIZED]: ERROR_CODES.UNAUTHORIZED,
  [HTTP_STATUS.FORBIDDEN]: ERROR_CODES.FORBIDDEN,
  [HTTP_STATUS.NOT_FOUND]: ERROR_CODES.NOT_FOUND,
  [HTTP_STATUS.METHOD_NOT_ALLOWED]: ERROR_CODES.METHOD_NOT_ALLOWED,
  [HTTP_STATUS.CONFLICT]: ERROR_CODES.CONFLICT,
  [HTTP_STATUS.PAYLOAD_TOO_LARGE]: ERROR_CODES.PAYLOAD_TOO_LARGE,
  [HTTP_STATUS.UNSUPPORTED_MEDIA_TYPE]: ERROR_CODES.UNSUPPORTED_MEDIA_TYPE,
  [HTTP_STATUS.TOO_MANY_REQUESTS]: ERROR_CODES.RATE_LIMITED,
};

/** "This coupon can't be used on this order" — checkout drops the coupon and keeps the order. */
export const COUPON_REFUSAL_CODES = [
  ERROR_CODES.INVALID_COUPON,
  ERROR_CODES.COUPON_NOT_ACTIVE,
  ERROR_CODES.COUPON_SCHEDULED,
  ERROR_CODES.COUPON_EXPIRED,
  ERROR_CODES.COUPON_USED_UP,
  ERROR_CODES.COUPON_FIRST_ORDER,
  ERROR_CODES.COUPON_LIMIT_REACHED,
  ERROR_CODES.COUPON_MIN_SUBTOTAL,
  ERROR_CODES.COUPON_NO_SAVING,
  ERROR_CODES.COUPON_SIGN_IN,
  ERROR_CODES.COUPON_NOT_ELIGIBLE,
] as const;
export type CouponRefusalCode = (typeof COUPON_REFUSAL_CODES)[number];

export function isCouponRefusalCode(code: string): code is CouponRefusalCode {
  return (COUPON_REFUSAL_CODES as readonly string[]).includes(code);
}
